/**
 * Tests — a simulated client talks to the adviser's real widget, then an expert reviewer (who knows the
 * field from iMaven's reference agent) judges the conversation.
 *
 *   1. The client: the adviser describes it in their own words, or presses "Write it with AI" and gets a
 *      full client (name, goal, background) to read and edit before running.
 *   2. The conversation is shown live while it runs — open by default, can be hidden; the agent and the
 *      client in different colours.
 *   3. When it ends: the reviewer's findings, here and in the chat, as SUGGESTIONS (2026-10-22 — the reviewer
 *      can be wrong): "did not follow your setup", "suggestion" or "iMaven's side". The adviser picks the ones
 *      to fix (drafted as proposals) and declines the rest — a declined one is never suggested again, and can
 *      be taken back from the list below.
 * Runs cost the adviser tokens.
 *
 * API: POST /agent-builder/tests/scenario · POST /agent-builder/tests {persona?, scenario?, pathway, variant} ·
 *      GET /agent-builder/tests · GET /agent-builder/tests/:id · POST .../cancel · POST .../fix {findings} ·
 *      POST .../findings/:n/dismiss {reason} · GET /agent-builder/review-dismissals · DELETE /agent-builder/review-dismissals/:id
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../Context/ToastContext';
import { Box, Row, Pill, Muted, Help, SmallBtn, PrimaryBtn, GhostBtn, TextArea, Input, Select, Empty, errText, fmtWhen } from './styles';

const VERDICT = { ok: ['Reviewer satisfied', 'rgba(63,185,80,0.18)', '#56d364'], 'needs-attention': ['Needs attention', 'rgba(210,153,34,0.18)', '#e3b341'] };
const SEV = { high: '#ff7b72', medium: '#e3b341', low: '#8b98a5' };
const KIND = {
  deviation: ['Did not follow your setup', 'rgba(248,81,73,0.16)', '#ff7b72'],
  suggestion: ['Suggestion', 'rgba(47,129,247,0.16)', '#79c0ff'],
  platform: ['iMaven’s side', 'rgba(139,152,165,0.16)', '#8b98a5'],
};
const runLabel = (r) => {
  if (r.status === 'running') return '…';
  if (r.status !== 'done') return r.status;
  const parts = [r.deviations ? `${r.deviations} to fix` : '', r.suggestions ? `${r.suggestions} suggestion${r.suggestions === 1 ? '' : 's'}` : ''].filter(Boolean);
  return parts.length ? parts.join(', ') : '✓';
};
const PHASE = { preparing: 'Writing the client…', identification: 'The client introduces themselves…', diagnostic: 'Conversation in progress…', reviewing: 'The reviewer is reading it…', complete: 'Done', error: 'Failed', cancelled: 'Cancelled' };
const AGENT_STYLE = { background: 'rgba(47,129,247,0.14)', border: '1px solid rgba(47,129,247,0.35)', color: '#cfe2ff' };
const CLIENT_STYLE = { background: 'rgba(63,185,80,0.12)', border: '1px solid rgba(63,185,80,0.35)', color: '#d2f5dc' };

function Line({ who, text }) {
  const agent = who === 'agent';
  return (
    <div style={{ display: 'flex', justifyContent: agent ? 'flex-start' : 'flex-end', margin: '6px 0' }}>
      <div style={{ maxWidth: '88%', borderRadius: 10, padding: '7px 10px', fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', ...(agent ? AGENT_STYLE : CLIENT_STYLE) }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', opacity: 0.75, marginBottom: 2 }}>{agent ? 'YOUR AGENT' : 'CLIENT'}</div>
        {text}
      </div>
    </div>
  );
}

export default function TestsPanel({ api, pathways, hasWidget, onChanged, focusRunId }) {
  const { showToast } = useToast();
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(focusRunId || null);
  const [run, setRun] = useState(null);
  const [pathway, setPathway] = useState('');
  const [variant, setVariant] = useState('typical');
  const [persona, setPersona] = useState({ name: '', goal: '', profile: '' });
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState('');
  const [showConversation, setShowConversation] = useState(true);
  const [declined, setDeclined] = useState([]);
  const [showDeclined, setShowDeclined] = useState(false);
  const endRef = useRef(null);

  const loadRuns = useCallback(async () => {
    try { const r = await api.get('/agent-builder/tests'); setRuns(r.data?.runs || []); } catch { /* keep */ }
  }, [api]);
  const loadRun = useCallback(async (id) => {
    if (!id) return;
    try { const r = await api.get(`/agent-builder/tests/${id}`); setRun(r.data); } catch { setRun(null); }
  }, [api]);

  const loadDeclined = useCallback(async () => {
    try { const r = await api.get('/agent-builder/review-dismissals'); setDeclined(r.data?.dismissals || []); } catch { /* keep */ }
  }, [api]);

  useEffect(() => { loadRuns(); loadDeclined(); }, [loadRuns, loadDeclined]);
  useEffect(() => { if (focusRunId) setSelected(focusRunId); }, [focusRunId]);
  useEffect(() => { setPicked([]); loadRun(selected); }, [selected, loadRun]);
  // While a test runs the conversation grows on screen (every 4 s).
  useEffect(() => {
    if (!run || run.status !== 'running') return undefined;
    const t = setInterval(() => { loadRun(run.runId); }, 4000);
    return () => clearInterval(t);
  }, [run, loadRun]);
  useEffect(() => { if (run?.status === 'running' && showConversation) endRef.current?.scrollIntoView({ block: 'nearest' }); }, [run?.transcript?.length, run?.status, showConversation]);
  useEffect(() => { if (run && run.status !== 'running') loadRuns(); }, [run?.status, loadRuns]); // eslint-disable-line react-hooks/exhaustive-deps

  const writeWithAi = async () => {
    setBusy('write');
    try {
      const r = await api.post('/agent-builder/tests/scenario', { scenario: persona.profile.trim() || persona.goal.trim() || undefined, pathway: pathway || undefined, variant });
      setPersona({ name: r.data.persona.name || '', goal: r.data.persona.goal || '', profile: r.data.persona.profile || '' });
      if (!pathway && r.data.pathway) setPathway(r.data.pathway);
      showToast('Here is a client — read it, change anything, then run the test.', 'success');
    } catch (e) { showToast(errText(e, 'Could not write a client'), 'error'); } finally { setBusy(''); }
  };

  const start = async () => {
    setBusy('run');
    try {
      const custom = persona.profile.trim().length >= 20;
      const r = await api.post('/agent-builder/tests', custom
        ? { persona, pathway: pathway || undefined, variant }
        : { scenario: persona.goal.trim() || undefined, pathway: pathway || undefined, variant });
      showToast(`Test started on “${r.data.programName}”. Watch the conversation below.`, 'success');
      await loadRuns();
      setSelected(r.data.runId);
      setShowConversation(true);
      onChanged?.();
    } catch (e) { showToast(errText(e, 'The test could not start'), 'error'); } finally { setBusy(''); }
  };
  const fix = async () => {
    setBusy('fix');
    try {
      const r = await api.post(`/agent-builder/tests/${run.runId}/fix`, { findings: picked.length ? picked : undefined });
      showToast(`Preparing fixes for ${r.data.findings} finding${r.data.findings === 1 ? '' : 's'} — they will appear under Proposals.`, 'success');
      onChanged?.();
    } catch (e) { showToast(errText(e, 'Could not prepare fixes'), 'error'); } finally { setBusy(''); }
  };
  const decline = async (f) => {
    const reason = window.prompt(`Don't suggest “${f.title}” again?\n\nWhy (optional — the reviewer reads it):`, '');
    if (reason === null) return;
    try {
      await api.post(`/agent-builder/tests/${run.runId}/findings/${f.number}/dismiss`, { reason });
      setPicked(p => p.filter(x => x !== f.number));
      showToast('Got it — it will not be suggested again.', 'success');
      await Promise.all([loadRun(run.runId), loadDeclined(), loadRuns()]);
    } catch (e) { showToast(errText(e, 'Could not decline it'), 'error'); }
  };
  const takeBack = async (d) => {
    try { await api.delete(`/agent-builder/review-dismissals/${d.id}`); await loadDeclined(); showToast('The reviewer may suggest it again.', 'info'); } catch (e) { showToast(errText(e, 'Could not take it back'), 'error'); }
  };
  const cancel = async () => {
    try { await api.post(`/agent-builder/tests/${run.runId}/cancel`); showToast('Stopping the test…', 'info'); } catch (e) { showToast(errText(e, 'Could not cancel'), 'error'); }
  };

  const fixable = (run?.findings || []).filter(f => f.who !== 'imaven' && !f.dismissed);
  const [vText, vBg, vFg] = VERDICT[run?.verdict] || [];
  const transcript = run?.transcript || [];

  return (
    <div>
      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>The client to simulate</div>
        {!hasWidget && <Help style={{ color: '#e3b341', marginBottom: 8 }}>Your widget is not set up yet — tests run against your real widget, so set it up first.</Help>}
        <Help style={{ marginBottom: 8 }}>Write the client yourself, or let AI write one (it uses what the builder knows about your practice). Either way you can edit it before running.</Help>
        <Input placeholder="Name (optional)" value={persona.name} onChange={e => setPersona(p => ({ ...p, name: e.target.value }))} style={{ marginBottom: 6 }} />
        <Input placeholder="What they want, in one sentence — e.g. “a full-time general labour job, starting next week”" value={persona.goal} onChange={e => setPersona(p => ({ ...p, goal: e.target.value }))} style={{ marginBottom: 6 }} />
        <TextArea rows={5} placeholder="Who they are and their situation, in first person — work history, skills, where they live, availability, anything that matters. (Leave empty and press “Write it with AI” for a full client.)" value={persona.profile} onChange={e => setPersona(p => ({ ...p, profile: e.target.value }))} />
        <Row style={{ marginTop: 8 }}>
          <Select value={pathway} onChange={e => setPathway(e.target.value)}>
            <option value="">Route: choose automatically</option>
            {(pathways || []).map(p => <option key={p.id} value={p.id}>{p.displayName}</option>)}
          </Select>
          <Select value={variant} onChange={e => setVariant(e.target.value)}>
            <option value="typical">Typical client</option>
            <option value="eligible">Strong case</option>
            <option value="borderline">Borderline case</option>
            <option value="ineligible">Weak case</option>
          </Select>
        </Row>
        <Row style={{ marginTop: 8, justifyContent: 'space-between' }}>
          <Row>
            <GhostBtn disabled={!!busy} onClick={writeWithAi}>{busy === 'write' ? 'Writing…' : '✨ Write it with AI'}</GhostBtn>
            {(persona.profile || persona.goal || persona.name) && <SmallBtn onClick={() => setPersona({ name: '', goal: '', profile: '' })}>Clear</SmallBtn>}
          </Row>
          <PrimaryBtn disabled={!!busy || !hasWidget} onClick={start}>{busy === 'run' ? 'Starting…' : 'Run test'}</PrimaryBtn>
        </Row>
        <Help>A test uses your AI tokens (a conversation as long as your interview, plus the review). Writing a client with AI uses a little too.</Help>
      </Box>

      {runs.length === 0 ? <Empty>No tests yet.</Empty> : (
        <Row style={{ marginBottom: 10 }}>
          {runs.map(r => (
            <SmallBtn key={r.runId} onClick={() => setSelected(r.runId)} style={{ borderColor: selected === r.runId ? '#2f81f7' : undefined }}>
              {r.programName} · {runLabel(r)}
            </SmallBtn>
          ))}
        </Row>
      )}

      {run && (
        <Box>
          <Row style={{ justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 600 }}>{run.programName}</div>
            {run.status === 'running'
              ? <Row><Pill>{PHASE[run.phase] || run.phase}</Pill><SmallBtn onClick={cancel}>Stop</SmallBtn></Row>
              : vText ? <Pill $bg={vBg} $fg={vFg}>{vText}</Pill> : <Pill>{PHASE[run.phase] || run.status}</Pill>}
          </Row>
          {run.persona && (
            <Help style={{ marginTop: 6 }}>
              <b>Client:</b> {run.persona.name}{run.persona.goal ? ` — ${run.persona.goal}` : ''}
            </Help>
          )}
          {run.error && <Help style={{ color: '#ff7b72' }}>{run.error}</Help>}

          <Row style={{ justifyContent: 'space-between', marginTop: 10 }}>
            <Muted>{transcript.length ? `Conversation · ${run.turns} turn${run.turns === 1 ? '' : 's'}${run.costUsd != null ? ` · $${Number(run.costUsd).toFixed(2)}` : ''}` : run.status === 'running' ? 'The conversation will appear here as it happens…' : ''}</Muted>
            {transcript.length > 0 && <SmallBtn onClick={() => setShowConversation(s => !s)}>{showConversation ? 'Hide conversation' : 'Show conversation'}</SmallBtn>}
          </Row>
          {showConversation && transcript.length > 0 && (
            <div style={{ maxHeight: 420, overflowY: 'auto', marginTop: 6, padding: '4px 2px' }}>
              {transcript.map((t, i) => (
                <React.Fragment key={i}>
                  {i === 0 && t.stage === 'opening' && <Muted style={{ display: 'block', margin: '4px 0' }}>Opening (identification)</Muted>}
                  {i > 0 && t.stage === 'interview' && transcript[i - 1].stage === 'opening' && <Muted style={{ display: 'block', margin: '8px 0 4px' }}>Interview</Muted>}
                  {t.agent && <Line who="agent" text={t.agent} />}
                  {t.client && <Line who="client" text={t.client} />}
                </React.Fragment>
              ))}
              {run.status === 'running' && <Muted style={{ display: 'block', margin: 6 }}>…</Muted>}
              <div ref={endRef} />
            </div>
          )}

          {run.status !== 'running' && (run.summary || (run.findings || []).length > 0) && (
            <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>The reviewer’s verdict</div>
              {run.summary && <div style={{ fontSize: 13, lineHeight: 1.5, margin: '4px 0 8px' }}>{run.summary}</div>}
              {(run.findings || []).map(f => {
                const [kText, kBg, kFg] = KIND[f.kind] || KIND.suggestion;
                const mine = f.who !== 'imaven';
                return (
                  <div key={f.number} style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '8px 0', display: 'flex', gap: 8, opacity: f.dismissed ? 0.5 : 1 }}>
                    {mine && !f.dismissed
                      ? <input type="checkbox" checked={picked.includes(f.number)} onChange={e => setPicked(p => (e.target.checked ? [...p, f.number] : p.filter(x => x !== f.number)))} style={{ marginTop: 3 }} />
                      : <span style={{ width: 13 }} />}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Row style={{ justifyContent: 'space-between', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: SEV[f.severity] || '#e6edf3' }}>{f.number}. {f.title}</div>
                        <Pill $bg={kBg} $fg={kFg}>{f.dismissed ? 'Declined' : kText}</Pill>
                      </Row>
                      <Help style={{ marginTop: 2 }}>{f.detail}</Help>
                      {!mine ? <Help>On iMaven’s side — nothing needed from you.</Help> : !f.dismissed && f.steps?.length > 0 && <Help>Suggested: {f.steps.join(' · ')}</Help>}
                      {mine && !f.dismissed && run.status === 'done' && <SmallBtn style={{ marginTop: 6 }} onClick={() => decline(f)}>Don’t suggest this again</SmallBtn>}
                    </div>
                  </div>
                );
              })}
              {run.suppressed > 0 && <Help>{run.suppressed} point{run.suppressed === 1 ? '' : 's'} you declined before {run.suppressed === 1 ? 'was' : 'were'} not raised again.</Help>}
              {run.status === 'done' && fixable.length > 0 && (
                <>
                  <Help>These are suggestions, not obligations — the reviewer can be wrong. Tick the ones you want fixed: the builder drafts them as proposals for your approval. Decline the ones you disagree with and they will not come back.</Help>
                  <Row style={{ marginTop: 6 }}>
                    <GhostBtn disabled={!!busy || !picked.length} onClick={fix}>{busy === 'fix' ? 'Preparing…' : picked.length ? `Draft fixes for ${picked.length} selected` : 'Tick the suggestions to fix'}</GhostBtn>
                  </Row>
                </>
              )}
            </div>
          )}
          <Help>{fmtWhen(runs.find(r => r.runId === run.runId)?.at)}</Help>
        </Box>
      )}

      {declined.length > 0 && (
        <Box>
          <Row style={{ justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Suggestions you declined ({declined.length})</div>
            <SmallBtn onClick={() => setShowDeclined(s => !s)}>{showDeclined ? 'Hide' : 'Show'}</SmallBtn>
          </Row>
          <Help>The reviewer is told about these and does not raise them again. Take one back if you changed your mind.</Help>
          {showDeclined && declined.map(d => (
            <Row key={d.id} style={{ justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '6px 0', flexWrap: 'nowrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13 }}>{d.title}</div>
                <Muted>{d.reason ? `Your reason: ${d.reason} · ` : ''}{fmtWhen(d.at)}</Muted>
              </div>
              <SmallBtn onClick={() => takeBack(d)}>Take back</SmallBtn>
            </Row>
          ))}
        </Box>
      )}
    </div>
  );
}
